import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import AdminNav from '../components/AdminNav';
import ReviewerAccountControls from '../components/ReviewerAccountControls';
import useAdminPermissions from '../hooks/useAdminPermissions';
import { reviewerService } from '../services/api';

const emptyForm = { firstName: '', lastName: '', email: '', expertise: '', affiliation: '' };
const fields = [
  ['firstName', 'نام', 100], ['lastName', 'نام خانوادگی', 100],
  ['email', 'ایمیل', 254], ['expertise', 'تخصص', 1000], ['affiliation', 'وابستگی سازمانی', 300],
];

export default function AdminReviewers() {
  const session = useAdminPermissions();
  const canView = session.can('reviewers.view');
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [expired, setExpired] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (!canView) return;
    let active = true;
    reviewerService.getAll().then(({ data }) => {
      if (active) setReviewers(data);
    }).catch(err => {
      if (!active) return;
      if (err.response?.status === 401) setExpired(true);
      else if (err.response?.status === 403) setDenied(true);
      else setError('دریافت فهرست داوران ناموفق بود. صفحه را دوباره بارگذاری کنید.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [canView]);

  const reportError = err => {
    if (err.response?.status === 401) setExpired(true);
    else if (err.response?.status === 403) {
      setEditing(null);
      setError('اجازه انجام این عملیات را ندارید. برای دریافت دسترسی‌های جدید صفحه را دوباره بارگذاری کنید.');
    } else if (err.response?.status === 409) setError(err.response?.data?.message || 'داور دارای سابقه داوری است و قابل حذف نیست.');
    else if (err.response?.status === 404) setError('این داور دیگر موجود نیست. صفحه را دوباره بارگذاری کنید.');
    else if (err.response?.status === 400) setError('اطلاعات واردشده معتبر نیست. نام، ایمیل و سایر فیلدها را بررسی کنید.');
    else setError('ذخیره تغییرات ناموفق بود. دوباره تلاش کنید.');
  };

  const save = async event => {
    event.preventDefault();
    setBusy(true); setError(''); setNotice('');
    const data = Object.fromEntries(fields.map(([key]) => [key, form[key].trim()]));
    try {
      const response = editing === 'new'
        ? await reviewerService.create(data) : await reviewerService.update(editing, data);
      setReviewers(current => editing === 'new' ? [...current, response.data]
        : current.map(item => item.id === editing ? response.data : item));
      setEditing(null); setForm(emptyForm); setNotice('اطلاعات داور ذخیره شد.');
    } catch (err) { reportError(err); }
    finally { setBusy(false); }
  };

  const remove = async reviewer => {
    if (!window.confirm(`آیا از حذف ${reviewer.firstName} ${reviewer.lastName} مطمئن هستید؟`)) return;
    setBusy(true); setError(''); setNotice('');
    try {
      await reviewerService.delete(reviewer.id);
      setReviewers(current => current.filter(item => item.id !== reviewer.id));
      if (editing === reviewer.id) setEditing(null);
      setNotice('داور حذف شد.');
    } catch (err) { reportError(err); }
    finally { setBusy(false); }
  };

  if (session.loading) return <p className="p-8" role="status">در حال بررسی دسترسی…</p>;
  if (session.status === 401 || expired) return <Navigate to="/admin-login" replace />;
  if (session.status !== 200) return <p className="p-8" role="alert">بررسی دسترسی ناموفق بود. صفحه را دوباره بارگذاری کنید.</p>;
  if (!canView || denied) return <div dir="rtl" className="p-8"><p role="alert">اجازه مشاهده فهرست داوران را ندارید.</p><Link to="/admin/articles">بازگشت به مدیریت</Link></div>;

  return <div dir="rtl" className="min-h-screen bg-gray-100">
    <AdminNav />
    <div className="max-w-6xl mx-auto p-4 sm:p-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">مدیریت داوران</h1>
        {session.can('reviewers.create') && <button disabled={busy || loading} className="bg-blue-700 text-white px-4 py-2 rounded disabled:opacity-50" onClick={() => { setEditing('new'); setForm(emptyForm); setError(''); setNotice(''); }}>افزودن داور</button>}
      </div>
      <p className="text-gray-600 mb-4">اطلاعات تماس و تخصص داوران برای استفاده تحریریه است. داور دارای سابقه داوری قابل حذف نیست.</p>
      {error && <p role="alert" className="bg-red-50 text-red-800 p-3 rounded mb-4">{error}</p>}
      {notice && <p role="status" className="bg-green-50 text-green-800 p-3 rounded mb-4">{notice}</p>}
      {editing !== null && <form onSubmit={save} className="bg-white rounded-lg shadow p-5 mb-6">
        <h2 className="text-lg font-bold mb-4">{editing === 'new' ? 'داور جدید' : 'ویرایش داور'}</h2>
        <fieldset disabled={busy} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fields.map(([key, label, max]) => <label key={key} className="block">{label}
            <input name={key} type={key === 'email' ? 'email' : 'text'} dir={key === 'email' ? 'ltr' : 'rtl'} required maxLength={max} value={form[key]} onChange={event => setForm({ ...form, [key]: event.target.value })} className="mt-1 border rounded p-2 w-full" />
          </label>)}
          <div className="flex gap-3 sm:col-span-2">
            <button type="submit" className="bg-blue-700 text-white px-4 py-2 rounded">{busy ? 'در حال ذخیره…' : 'ذخیره'}</button>
            <button type="button" className="bg-gray-200 px-4 py-2 rounded" onClick={() => setEditing(null)}>انصراف</button>
          </div>
        </fieldset>
      </form>}
      {loading ? <p role="status">در حال دریافت داوران…</p> : reviewers.length === 0 ? <p className="bg-white p-6 rounded">هنوز داوری ثبت نشده است.</p> :
        <ul className="space-y-4">{reviewers.map(reviewer => <li key={reviewer.id} className="bg-white shadow rounded-lg p-5 break-words">
          <h2 className="font-bold text-lg">{reviewer.firstName} {reviewer.lastName}</h2>
          <p className="text-gray-700 mt-2">تخصص: {reviewer.expertise}</p>
          <p className="text-gray-700">وابستگی سازمانی: {reviewer.affiliation}</p>
          <p className="text-gray-600"><bdi>{reviewer.email}</bdi></p>
          <ReviewerAccountControls reviewer={reviewer} session={session} onError={reportError} onChange={updated => setReviewers(current => current.map(item => item.id === updated.id ? updated : item))} />
          <div className="flex gap-4 mt-3">
            {session.can('reviewers.update') && <button disabled={busy} className="text-blue-700" aria-label={`ویرایش ${reviewer.firstName} ${reviewer.lastName}`} onClick={() => { setEditing(reviewer.id); setForm(Object.fromEntries(fields.map(([key]) => [key, reviewer[key]]))); setError(''); setNotice(''); window.scrollTo(0, 0); }}>ویرایش</button>}
            {session.can('reviewers.delete') && <button disabled={busy} className="text-red-700" aria-label={`حذف ${reviewer.firstName} ${reviewer.lastName}`} onClick={() => remove(reviewer)}>حذف</button>}
          </div>
        </li>)}</ul>}
    </div>
  </div>;
}
